import { ISearchResult } from 'types/searchInterfaces'

export type MatchQuality = 'strong' | 'moderate' | 'weak'

// Must match caleo-back's CV_SEARCH_KEYWORD_SCORE_THRESHOLD default (search.ts). A candidate
// admitted purely via the semantic path can still carry a residual keyword_score from a single
// incidental common-word match (~0.1 per matched term, via Postgres's ts_rank_cd default weight) -
// that's exactly the noise the backend's admission floor filters out, so it must not count here
// as genuine keyword corroboration, or a semantically-admitted but irrelevant result gets
// miscounted as a two-signal "strong" match.
const MIN_KEYWORD_SCORE_FOR_MATCH = 0.15

// Raw cosine similarity (semantic_score) clusters in a narrow high band for this embedding model
// regardless of true relevance - a genuinely irrelevant query can still score ~0.80-0.83 against
// any CV, barely below a relevant query's ~0.84-0.88. semantic_zscore instead expresses a
// candidate relative to this specific query's own top-500 candidate pool (computed server-side),
// so an irrelevant query - whose whole pool is uniformly mediocre, with no outliers - doesn't get
// treated as a real semantic match just because everything clears the same absolute floor. 1
// standard deviation above the pool's own mean is a first-pass bar, not yet validated against
// judged-relevant data; revisit if it proves too strict or too lax in practice.
const MIN_SEMANTIC_ZSCORE_FOR_MATCH = 1

/**
 * Derives a match quality tier from which signals backed a free text search result. Matching on
 * both semantic similarity and keywords means two independent signals agree (strong). Semantic-only
 * means the embedding considers it conceptually related even without shared wording (moderate).
 * Keyword-only means it only shares words with the query, unconfirmed by meaning - the case most
 * likely to be an incidental, not-actually-relevant match (weak).
 *
 * If the (optional, cost-gated) LLM reranking step judged this result explicitly not relevant, that
 * overrides the statistical tiers down to weak - it exists specifically to catch same-word-different-
 * meaning cases neither keyword nor semantic matching can tell apart on their own (e.g. "Worker" as a
 * generic job title vs. a search for farm workers). It only ever downgrades, never upgrades: an LLM
 * judgment of "relevant" doesn't override a statistically weak result, since the goal is catching
 * false positives, not manufacturing false confidence.
 *
 * @param result - The free text search result to grade.
 * @returns The match quality tier.
 */
export const deriveMatchQuality = (
  result: Pick<ISearchResult, 'semantic_zscore' | 'keyword_score' | 'llmRelevant'>
): MatchQuality => {
  if (result.llmRelevant === false) {
    return 'weak'
  }

  const matchedViaSemantic = (result.semantic_zscore ?? -Infinity) > MIN_SEMANTIC_ZSCORE_FOR_MATCH
  const matchedViaKeyword = (result.keyword_score ?? 0) > MIN_KEYWORD_SCORE_FOR_MATCH

  if (matchedViaSemantic && matchedViaKeyword) {
    return 'strong'
  }
  return matchedViaSemantic ? 'moderate' : 'weak'
}

const QUALITY_SORT_ORDER: Record<MatchQuality, number> = { strong: 0, moderate: 1, weak: 2 }

// Exact/partial (structured token match against the CV) ranks above free text before quality is
// even considered - an exact match is a stronger signal than any semantic/keyword-derived tier.
// Legacy free text results are all 'freeText', so this is a no-op tie within that list and quality
// tier decides everything, same as before combined search introduced `kind`.
const KIND_SORT_ORDER: Record<ISearchResult['kind'], number> = { exact: 0, partial: 1, freeText: 2 }

/**
 * Sorts results by match kind first (exact, then partial/free text), then by match quality tier
 * within that kind (strong, then moderate, then weak), falling back to the backend's RRF `score`
 * as a final tie-breaker. Needed because the RRF score fuses on rank position across both the
 * semantic and keyword rankers, so a result with a small incidental keyword contribution can
 * outrank a higher-*quality* result even though its quality tier is worse - without this, weak
 * results can appear ahead of strong ones, or partial matches ahead of exact ones.
 *
 * @param results - The results to sort (input order is not mutated; a new array is returned).
 * @returns A new array sorted by kind, then quality tier, then score.
 */
export const sortByMatchQuality = <T extends ISearchResult>(results: T[]): T[] =>
  [...results].sort((a, b) => {
    const kindDiff = KIND_SORT_ORDER[a.kind] - KIND_SORT_ORDER[b.kind]
    if (kindDiff !== 0) {
      return kindDiff
    }
    const tierDiff = QUALITY_SORT_ORDER[deriveMatchQuality(a)] - QUALITY_SORT_ORDER[deriveMatchQuality(b)]
    if (tierDiff !== 0) {
      return tierDiff
    }
    return (b.score ?? 0) - (a.score ?? 0)
  })
